import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { IdCard, RefreshCw, CircleCheck, CircleX, X, RotateCcw, Undo2, Ban, Search, ArrowRight, ShieldAlert, Eraser, History, TriangleAlert, LoaderCircle } from 'lucide-react'
import {
  fetchCustomerIds, previewCustomerId, migrateCustomerId, fetchCustomerIdJournal, fetchCustomerIdChange,
  resumeCustomerIdChange, abortCustomerIdChange, undoCustomerIdChange, sweepCustomerIds
} from '../actions/customerids'
import '../styles/CustomerIdsView.css'

// «Clientes · ID» (ADMIN MASTER, /customer-ids). Waterproof is the original platform: customers created here before the
// shared auth database keep an id that is not their auth id – the one Lockers and Ticketing use. The list compares every
// customer with its auth account (same email) and Lockers; «Revisar» shows what the change touches and changes the id
// across Waterproof (customer, sub-accounts, contracts, machines, minis, sales, arqueos, incidences, tickets, objectives,
// codes, shipments). The change runs on the server, step by step, with a journal: resumable, cancellable before the
// copy, undoable when finished (API routes/v1/customerids.js · services/customerIds.js).

const STATUS = {
  PENDING: { label: 'Pendiente', tone: 'amber', hint: 'Su cuenta de auth (mismo email) tiene otro id: el que usan Lockers y Ticketing.' },
  CONFLICT: { label: 'Conflicto', tone: 'red', hint: 'El id de su cuenta de auth ya es otro cliente de Waterproof: hay que revisarlo a mano.' },
  NO_AUTH: { label: 'Sin auth', tone: 'gray', hint: 'No hay ninguna cuenta de cliente en auth con su email.' },
  ALIGNED: { label: 'Alineado', tone: 'green', hint: 'Su id es el de auth.' },
  PROTECTED: { label: 'Protegido', tone: 'blue', hint: 'Id fijado en el código: no se cambia.' }
}
const FILTERS = [['PENDING', 'Pendientes'], ['CONFLICT', 'Conflicto'], ['NO_AUTH', 'Sin auth'], ['ALIGNED', 'Alineados'], ['PROTECTED', 'Protegidos'], ['ALL', 'Todos']]
const STEP_LABELS = {
  hold: 'Liberar el email y el nº del cliente antiguo',
  insert: 'Copiar el cliente al id nuevo',
  references: 'Actualizar los documentos',
  remove: 'Borrar el cliente antiguo',
  alias: 'Alias id antiguo → nuevo (máquinas y API externa)',
  devices: 'Máquinas: recargar parámetros'
}
const STEPS = Object.keys(STEP_LABELS)
const JOURNAL_STATUS = { RUNNING: ['En curso', 'blue'], DONE: ['Hecho', 'green'], FAILED: ['Fallido', 'red'], ABORTED: ['Cancelado', 'gray'], UNDONE: ['Deshecho', 'gray'] }

const fmtDate = (d) => (d ? new Date(d).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—')
/** CALL_API result → { data } | { error, detail } (a 401 logs out and brings neither). */
const unwrap = (res) => (res && res.response ? { data: res.response } : { error: (res && res.error && res.error.message) || 'No se pudo completar (¿sesión caducada?)', detail: res && res.error })
const interrupted = (j) => j.status === 'RUNNING' && !j.running
const canAbort = (j) => (j.status === 'FAILED' || interrupted(j)) && !(j.steps || []).some((s) => s.name === 'insert')

const Chip = ({ tone, children, title }) => <span className={`cid-chip cid-${tone}`} title={title}>{children}</span>
const Id = ({ value }) => (value ? <code className="cid-id">{value}</code> : <span className="cid-muted">—</span>)

function lockersCell(r) {
  if (!r.lockers) return <span className="cid-muted">—</span>
  if (!r.lockers.found) return <span className="cid-muted">No está</span>
  if (r.lockers.id === r.id) return <span className="cid-ok">Mismo id</span>
  if (r.lockers.sameAsTarget) return <span className="cid-ok">Con el id de auth</span>
  return <Id value={r.lockers.id} />
}

/** The steps of a change, its result and – after a failure – resume / cancel. */
function Progress({ j, busy, onResume, onAbort }) {
  const done = new Map((j.steps || []).map((s) => [s.name, s]))
  const current = STEPS.find((s) => !done.has(s))
  const refs = done.get('references')
  return (
    <div className="cid-progress">
      <ol className="cid-steps">
        {STEPS.map((name) => {
          const s = done.get(name)
          const active = !s && name === current && j.status === 'RUNNING' && j.running
          const failed = !s && name === current && (j.status === 'FAILED' || interrupted(j))
          return (
            <li key={name} className={s ? 'is-done' : active ? 'is-active' : failed ? 'is-failed' : ''}>
              <span className="cid-step-icon">{s ? <CircleCheck size={16} /> : active ? <LoaderCircle size={16} className="cid-spin" /> : failed ? <CircleX size={16} /> : <span className="cid-dot" />}</span>
              <span>{STEP_LABELS[name]}{s && name !== 'alias' ? <span className="cid-muted"> · {s.count}</span> : ''}</span>
            </li>
          )
        })}
      </ol>
      {refs && refs.detail && (
        <div className="cid-refs-done">
          {refs.detail.filter((d) => d.count).map((d) => <span key={`${d.collection}.${d.path}`}>{d.label}: <b>{d.count}</b></span>)}
        </div>
      )}
      {j.status === 'DONE' && (
        <div className="cid-alert cid-alert-green"><CircleCheck size={16} /> <span>Hecho: el cliente tiene ahora el id <code>{j.to}</code>. Sus máquinas recargarán los parámetros; quien tenga la sesión abierta tendrá que volver a entrar.</span></div>
      )}
      {(j.status === 'FAILED' || interrupted(j)) && (
        <div className="cid-alert cid-alert-red">
          <CircleX size={16} />
          <div>
            <div>{j.status === 'FAILED' ? `Se ha parado: ${j.error || 'error desconocido'}` : 'Se interrumpió (el servidor se reinició).'} Lo hecho está anotado: al reanudar sigue desde el paso pendiente.</div>
            <div className="cid-actions">
              <button type="button" className="cid-btn cid-btn-primary" disabled={!!busy} onClick={onResume}><RotateCcw size={14} /> Reanudar</button>
              {canAbort(j) && <button type="button" className="cid-btn" disabled={!!busy} onClick={onAbort}><Ban size={14} /> Cancelar el cambio</button>}
            </div>
          </div>
        </div>
      )}
      {j.status === 'RUNNING' && j.running && <div className="cid-muted cid-small">El cambio sigue en el servidor aunque cierres esta ventana.</div>}
    </div>
  )
}

/** Review of one customer (what the change touches, blockers, confirmation) or the progress of a change. */
function ChangeModal({ row, watch, actions, onClose }) {
  const [preview, setPreview] = useState(null)
  const [error, setError] = useState('')
  const [snapshot, setSnapshot] = useState(false)
  const [confirm, setConfirm] = useState('')
  const [journalId, setJournalId] = useState(watch ? watch.journalId : '')
  const [j, setJ] = useState(null)
  const [poll, setPoll] = useState(0)
  const [busy, setBusy] = useState('')

  useEffect(() => {
    if (!row) return undefined
    let stop = false
    actions.previewCustomerId(row.id, row.target.id).then((res) => {
      if (stop) return
      const r = unwrap(res)
      if (r.data) setPreview(r.data)
      else setError(r.error)
    })
    return () => { stop = true }
  }, [row, actions])

  useEffect(() => {
    if (!journalId) return undefined
    let stop = false
    let timer = null
    const tick = async () => {
      const r = unwrap(await actions.fetchCustomerIdChange(journalId))
      if (stop) return
      if (r.error) { setError(r.error); return }
      setJ(r.data)
      if (r.data.status === 'RUNNING' && r.data.running) timer = setTimeout(tick, 800)
    }
    tick()
    return () => { stop = true; clearTimeout(timer) }
  }, [journalId, poll, actions])

  const expected = preview && preview.customer ? String(preview.customer.number === null ? preview.customer.id : preview.customer.number) : ''
  const ready = preview && !preview.blockers.length && snapshot && confirm.trim() === expected

  const start = async () => {
    setBusy('start')
    setError('')
    const r = unwrap(await actions.migrateCustomerId(row.id, row.target.id, confirm.trim()))
    setBusy('')
    if (r.error) {
      setError(r.error)
      if (r.detail && r.detail.check) setPreview(r.detail.check)
      return
    }
    setJournalId(r.data.journalId)
  }
  const resume = async () => {
    setBusy('resume')
    const r = unwrap(await actions.resumeCustomerIdChange(journalId))
    setBusy('')
    if (r.error) setError(r.error)
    else { setError(''); setPoll((n) => n + 1) }
  }
  const abort = async () => {
    if (!window.confirm('¿Cancelar el cambio? El cliente recupera su email y su nº y sigue con su id actual.')) return
    setBusy('abort')
    const r = unwrap(await actions.abortCustomerIdChange(journalId))
    setBusy('')
    if (r.error) setError(r.error)
    else setPoll((n) => n + 1)
  }

  const name = (row && row.fullname) || (watch && watch.customer && watch.customer.fullname) || ''
  const nonZero = preview ? preview.refs.filter((x) => x.count) : []
  const zero = preview ? preview.refs.filter((x) => !x.count) : []
  return (
    <div className="cid-backdrop" onClick={() => onClose(!!journalId)}>
      <div className="cid-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="cid-modal-head">
          <div className="cid-modal-title"><IdCard size={18} /> {watch ? watch.title : 'Cambiar el id'}{name ? ` · ${name}` : ''}</div>
          <button type="button" className="cid-icon-btn" aria-label="Cerrar" onClick={() => onClose(!!journalId)}><X size={18} /></button>
        </div>
        <div className="cid-modal-body">
          {error && <div className="cid-alert cid-alert-red"><CircleX size={16} /> <span>{error}</span></div>}
          {!journalId && !preview && !error && <div className="cid-muted"><LoaderCircle size={16} className="cid-spin" /> Calculando qué se actualiza…</div>}
          {!journalId && preview && (
            <>
              <div className="cid-ids">
                <div><div className="cid-label">ID actual en Waterproof</div><Id value={preview.from} /><div className="cid-small">{preview.customer ? `${preview.customer.email} · nº ${preview.customer.number === null ? '—' : preview.customer.number}` : ''}</div></div>
                <ArrowRight size={18} className="cid-muted" />
                <div><div className="cid-label">ID de auth (Lockers / Ticketing)</div><Id value={preview.to} /><div className="cid-small">{preview.auth ? `${preview.auth.email}${preview.auth.fullname ? ` · ${preview.auth.fullname}` : ''}` : ''}</div></div>
              </div>
              <div className="cid-small cid-muted">Lockers: {!preview.lockers.configured ? 'sin conexión configurada en el servidor' : !preview.lockers.ok ? `no se pudo leer${preview.lockers.error ? ` (${preview.lockers.error})` : ''}` : preview.lockers.found ? 'tiene un cliente con el id de auth' : 'no hay ningún cliente con el id de auth'}</div>
              {preview.blockers.length > 0 && (
                <div className="cid-alert cid-alert-red"><ShieldAlert size={16} /><ul>{preview.blockers.map((b) => <li key={b}>{b}</li>)}</ul></div>
              )}
              {preview.warnings.length > 0 && (
                <div className="cid-alert cid-alert-amber"><TriangleAlert size={16} /><ul>{preview.warnings.map((w) => <li key={w}>{w}</li>)}</ul></div>
              )}
              <div className="cid-section-title">Se actualizan {preview.total} documentos</div>
              <table className="cid-table cid-table-compact">
                <tbody>
                  <tr><td>Cliente</td><td className="cid-num">1</td></tr>
                  {nonZero.map((x) => <tr key={`${x.collection}.${x.path}`}><td>{x.label}</td><td className="cid-num">{x.count}</td></tr>)}
                </tbody>
              </table>
              {zero.length > 0 && <div className="cid-small cid-muted">Sin documentos: {zero.map((x) => x.label).join(', ')}.</div>}
              {!preview.blockers.length && (
                <div className="cid-confirm">
                  <label className="cid-check"><input type="checkbox" checked={snapshot} onChange={(e) => setSnapshot(e.target.checked)} /> Hay una copia de seguridad (snapshot) de la base de datos de Waterproof de hoy</label>
                  <label className="cid-field">
                    <span>Escribe el nº del cliente ({expected}) para confirmar</span>
                    <input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={expected} />
                  </label>
                </div>
              )}
            </>
          )}
          {journalId && j && <Progress j={j} busy={busy} onResume={resume} onAbort={abort} />}
        </div>
        <div className="cid-modal-foot">
          <button type="button" className="cid-btn" onClick={() => onClose(!!journalId)}>Cerrar</button>
          {!journalId && preview && !preview.blockers.length && (
            <button type="button" className="cid-btn cid-btn-primary" disabled={!ready || !!busy} onClick={start}>{busy === 'start' ? 'Iniciando…' : 'Cambiar ID'}</button>
          )}
        </div>
      </div>
    </div>
  )
}

const CustomerIdsView = ({ actions }) => {
  const [data, setData] = useState(null)
  const [journal, setJournal] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('PENDING')
  const [query, setQuery] = useState('')
  const [modal, setModal] = useState(null)
  const [notice, setNotice] = useState(null)
  const [busy, setBusy] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    const [list, hist] = await Promise.all([actions.fetchCustomerIds(), actions.fetchCustomerIdJournal()])
    const l = unwrap(list)
    const h = unwrap(hist)
    setError(l.error || h.error || '')
    if (l.data) setData(l.data)
    if (h.data) setJournal(h.data)
    setLoading(false)
  }, [actions])

  useEffect(() => { load() }, [load])

  const rows = useMemo(() => {
    if (!data) return []
    const q = query.trim().toLowerCase()
    return data.rows.filter((r) => (filter === 'ALL' || r.status === filter)
      && (!q || [r.fullname, r.email, r.id, r.target && r.target.id, r.number].some((v) => v !== null && v !== undefined && String(v).toLowerCase().includes(q))))
  }, [data, filter, query])

  const closeModal = (changed) => {
    setModal(null)
    if (changed) load()
  }
  const journalAction = async (j, kind) => {
    const ask = {
      resume: '¿Reanudar el cambio desde el paso pendiente?',
      abort: '¿Cancelar el cambio? El cliente recupera su email y su nº y sigue con su id actual.',
      undo: `¿Deshacer el cambio de ${j.customer ? j.customer.fullname : 'este cliente'}? Vuelve a su id anterior (${j.from}) con todos sus documentos.`
    }[kind]
    if (!window.confirm(ask)) return
    setBusy(`${j._id}.${kind}`)
    const fn = { resume: actions.resumeCustomerIdChange, abort: actions.abortCustomerIdChange, undo: actions.undoCustomerIdChange }[kind]
    const r = unwrap(await fn(j._id))
    setBusy('')
    if (r.error) { setNotice({ tone: 'red', text: r.error }); return }
    if (kind === 'abort') { setNotice({ tone: 'green', text: 'Cambio cancelado: el cliente recupera su email y su nº.' }); load(); return }
    setNotice(null)
    setModal({ watch: { journalId: r.data.journalId, title: kind === 'undo' ? 'Deshacer el cambio' : 'Reanudar el cambio', customer: j.customer } })
  }
  const sweep = async () => {
    if (!window.confirm('¿Buscar documentos que hayan llegado con un id antiguo después de su cambio y pasarlos al id actual?')) return
    setBusy('sweep')
    const r = unwrap(await actions.sweepCustomerIds())
    setBusy('')
    if (r.error) setNotice({ tone: 'red', text: r.error })
    else setNotice({ tone: 'green', text: r.data.total ? `${r.data.total} documentos pasados al id actual (${r.data.detail.map((d) => `${d.label}: ${d.count}`).join(', ')}).` : 'No había documentos con ids antiguos.' })
  }

  const counts = (data && data.counts) || {}
  const total = data ? data.rows.length : 0
  return (
    <div className="cid-root">
      <div className="cid-head">
        <div>
          <h1 className="cid-title"><IdCard size={22} /> Clientes · ID</h1>
          <p className="cid-sub">Clientes de Waterproof frente a su cuenta de auth, el id que ya usan Lockers y Ticketing. Un cliente pendiente tiene aquí otro id: al cambiarlo se actualiza en todo Waterproof (cliente, subcuentas, contratos, máquinas, minis, ventas, arqueos, incidencias, tickets, objetivos, códigos y envíos).</p>
        </div>
        <button type="button" className="cid-btn" onClick={load} disabled={loading}><RefreshCw size={14} className={loading ? 'cid-spin' : ''} /> Actualizar</button>
      </div>

      {error && <div className="cid-alert cid-alert-red"><CircleX size={16} /> <span>{error}</span></div>}
      {notice && <div className={`cid-alert cid-alert-${notice.tone}`}>{notice.tone === 'green' ? <CircleCheck size={16} /> : <CircleX size={16} />} <span>{notice.text}</span></div>}

      {data && (
        <div className="cid-sources">
          <span className={data.auth.connected ? 'cid-ok' : 'cid-bad'}>Auth: {data.auth.connected ? `${data.auth.count} cuentas de cliente` : 'sin conexión'}</span>
          <span className={data.lockers.ok ? 'cid-ok' : 'cid-muted'}>Lockers: {!data.lockers.configured ? 'sin conexión configurada en el servidor' : data.lockers.ok ? 'leído' : `no se pudo leer${data.lockers.error ? ` (${data.lockers.error})` : ''}`}</span>
          <span className="cid-muted">{total} clientes en Waterproof</span>
        </div>
      )}

      <div className="cid-panel">
        <div className="cid-toolbar">
          <div className="cid-filters">
            {FILTERS.map(([key, label]) => (
              <button key={key} type="button" className={`cid-filter${filter === key ? ' active' : ''}`} onClick={() => setFilter(key)}>
                {label} <span className="cid-count">{key === 'ALL' ? total : counts[key] || 0}</span>
              </button>
            ))}
          </div>
          <label className="cid-search"><Search size={14} /><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Nombre, email, id o nº" /></label>
        </div>
        {loading && !data ? <div className="cid-empty"><LoaderCircle size={16} className="cid-spin" /> Cargando…</div> : (
          <table className="cid-table">
            <thead>
              <tr><th>Nº</th><th>Cliente</th><th>ID Waterproof</th><th>ID auth</th><th>Lockers</th><th>Estado</th><th /></tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const s = STATUS[r.status]
                const open = r.status === 'PENDING' ? () => { setNotice(null); setModal({ row: r }) } : null
                const lastChange = r.journal && (r.journal.status === 'FAILED' || r.journal.status === 'RUNNING') ? r.journal : null
                return (
                  <tr key={r.id} className={open ? 'is-clickable' : ''} onClick={open || undefined}>
                    <td className="cid-num">{r.number === null ? '—' : r.number}</td>
                    <td>
                      <div className="cid-name">{r.fullname || <span className="cid-muted">Sin nombre</span>}{r.blocked && <Chip tone="gray">Bloqueado</Chip>}</div>
                      <div className="cid-small cid-muted">{r.email}{r.type ? ` · ${r.type}` : ''}{r.parent ? ' · subcuenta' : ''}</div>
                    </td>
                    <td><Id value={r.id} />{r.idType !== 'ObjectId' && <div className="cid-small cid-muted">id {r.idType}</div>}</td>
                    <td>{r.status === 'ALIGNED' ? <span className="cid-ok">Mismo id</span> : <Id value={r.target && r.target.id} />}</td>
                    <td>{lockersCell(r)}</td>
                    <td>
                      <Chip tone={s.tone} title={r.protectedReason || s.hint}>{s.label}</Chip>
                      {lastChange && <div className="cid-small cid-bad">{lastChange.status === 'FAILED' ? 'Cambio fallido' : 'Cambio en curso'}</div>}
                    </td>
                    <td className="cid-right">{open && <button type="button" className="cid-btn cid-btn-primary cid-btn-sm" onClick={(e) => { e.stopPropagation(); open() }}>Revisar</button>}</td>
                  </tr>
                )
              })}
              {!rows.length && <tr><td colSpan={7} className="cid-empty">{filter === 'PENDING' && !query ? 'No queda ningún cliente pendiente.' : 'Ningún cliente con este filtro.'}</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      <div className="cid-panel">
        <div className="cid-panel-head">
          <div className="cid-panel-title"><History size={16} /> Cambios de id</div>
          <button type="button" className="cid-btn cid-btn-sm" disabled={busy === 'sweep'} onClick={sweep} title="Documentos que llegaron con un id antiguo después de su cambio → id actual"><Eraser size={14} /> Barrido de ids antiguos</button>
        </div>
        <table className="cid-table">
          <thead>
            <tr><th>Fecha</th><th>Cliente</th><th>ID antiguo → nuevo</th><th>Estado</th><th className="cid-num">Documentos</th><th>Por</th><th /></tr>
          </thead>
          <tbody>
            {journal.map((j) => {
              const [label, tone] = interrupted(j) ? ['Interrumpido', 'red'] : JOURNAL_STATUS[j.status] || [j.status, 'gray']
              return (
                <tr key={j._id} className="is-clickable" onClick={() => setModal({ watch: { journalId: j._id, title: j.reverseOf ? 'Deshacer el cambio' : 'Cambio de id', customer: j.customer } })}>
                  <td className="cid-nowrap">{fmtDate(j.startedAt)}</td>
                  <td>{j.customer ? j.customer.fullname : '—'}<div className="cid-small cid-muted">{j.customer ? j.customer.email : ''}{j.reverseOf ? ' · deshace un cambio' : ''}</div></td>
                  <td><Id value={j.from} /> <ArrowRight size={12} className="cid-muted" /> <Id value={j.to} /></td>
                  <td><Chip tone={tone} title={j.error || ''}>{label}</Chip></td>
                  <td className="cid-num">{j.references}</td>
                  <td>{j.startedBy ? j.startedBy.name : ''}</td>
                  <td className="cid-right cid-nowrap" onClick={(e) => e.stopPropagation()}>
                    {(j.status === 'FAILED' || interrupted(j)) && <button type="button" className="cid-btn cid-btn-sm" disabled={!!busy} onClick={() => journalAction(j, 'resume')}><RotateCcw size={13} /> Reanudar</button>}
                    {canAbort(j) && <button type="button" className="cid-btn cid-btn-sm" disabled={!!busy} onClick={() => journalAction(j, 'abort')}><Ban size={13} /> Cancelar</button>}
                    {j.status === 'DONE' && <button type="button" className="cid-btn cid-btn-sm" disabled={!!busy} onClick={() => journalAction(j, 'undo')}><Undo2 size={13} /> Deshacer</button>}
                  </td>
                </tr>
              )
            })}
            {!journal.length && <tr><td colSpan={7} className="cid-empty">Todavía no se ha cambiado ningún id.</td></tr>}
          </tbody>
        </table>
      </div>

      {modal && <ChangeModal row={modal.row} watch={modal.watch} actions={actions} onClose={closeModal} />}
    </div>
  )
}

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators({
    fetchCustomerIds, previewCustomerId, migrateCustomerId, fetchCustomerIdJournal, fetchCustomerIdChange,
    resumeCustomerIdChange, abortCustomerIdChange, undoCustomerIdChange, sweepCustomerIds
  }, dispatch)
})

export default connect(null, mapDispatchToProps)(CustomerIdsView)
